import {
  memo, useCallback, useContext, useEffect, useMemo, useRef, useState, type FunctionComponent, type ChangeEvent
} from 'react';
import toString from 'lodash/toString';
import { useMutation } from '@apollo/client';
import { Navigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';
// Skillmore UI Components
import { isValidEmail } from '@empathco/ui-components/src/helpers/strings';
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import { getEmail, saveEmail } from '@empathco/ui-components/src/helpers/login';
// local imports
import { VERIFY_EMAIL_QUERY } from '../graphql/VerifyEmail';
import { VerifyEmailDocument } from '../graphql/types';
import { PATH_LOGIN_LINK_SSO } from '../config/paths';
import { isFirebaseAuthEnabled } from '../config/firebase';
import useCustomerSettings from '../config/customer';
import useRedirects from '../helpers/redirect';
import { GlobalContext } from '../context/global';
import SplashText, { type SplashVariant } from '../elements/SplashText';
import Screen from '../v3/Screen';

const inputContainerSx: SxProps<Theme> = { width: { xs: '100%', sm: '28rem' }, mx: 'auto', pt: 4, px: 6 };

// Firebase-brokered SSO (SSO_AUTH_MODE=firebase): this screen both starts the SAML
// redirect — from the button, so the navigation is user-initiated — and is where the
// handler comes back to. `testAction` 4/5 let Storybook render it without an injected
// Firebase config; 5 is the state while the session check is still in flight.
const isFirebaseMode = (testAction?: number): boolean =>
  isFirebaseAuthEnabled() || (testAction !== undefined && testAction >= 4 && testAction <= 6);

// Firebase mode opens on the session check, so the button starts out pending.
const isInitiallyRedirecting = (testAction?: number): boolean =>
  testAction === 1 || testAction === 5 || isFirebaseAuthEnabled();

const splashVariant = (firebaseMode: boolean, hasMultipleSSO: boolean, waiting: boolean): SplashVariant =>
  (firebaseMode && 'sso') || (hasMultipleSSO && 'multiple_sso') || (waiting ? 'logging_in' : 'login');

type LoginScreenProps = {
  // for Storybook only
  readonly testAction?: number;
}

const LoginScreen: FunctionComponent<LoginScreenProps> = ({
  testAction
}) => {
  const { formatMessage } = useIntl();
  const { HAS_MULTIPLE_SSO, HAS_LOGIN } = useCustomerSettings();
  const { getRedirectionPath } = useRedirects();
  const hasMultipleSSO = HAS_MULTIPLE_SSO || !HAS_LOGIN;

  const inputRef = useRef<HTMLInputElement>(null);
  const { current } = inputRef;

  const {
    token,
    user: { data: user, pending, failed },
    authEmail: { pending: pendingAuthEmail, failed: failedAuthEmail, params }, sendAuthEmail,
    loginWithFirebase, completeFirebaseLogin
  } = useContext(GlobalContext);

  const firebaseMode = isFirebaseMode(testAction);

  // verifying email
  const { mutate: verifyEmail, loading: pendingVerify, failed: failedVerify } = useMutationMethod({
    // key: 'verifyEmail', // `success: false` means the email is invalid
    mutation: useMutation(VERIFY_EMAIL_QUERY as typeof VerifyEmailDocument)
  });

  const [email, setEmail] = useState<string>((hasMultipleSSO ? getEmail() : params?.email) || '');
  const [invalidEmail, setInvalidEmail] = useState(testAction === 3);
  const [pendingRedirect, setPendingRedirect] = useState(isInitiallyRedirecting(testAction));
  // Firebase mode: the SDK failed before the exchange could even be attempted, so
  // there is no query state to carry it. `onError` only clears the stored token.
  const [ssoFailed, setSsoFailed] = useState(testAction === 6);
  const [waiting, setWaiting] = useState(pendingAuthEmail === false && failedAuthEmail === false);

  const pendingAction = pendingVerify || pendingRedirect;

  const emailLabel = useMemo(() => formatMessage({ id: 'login.email' }), [formatMessage]);

  useEffect(() => {
    current?.focus?.();
  }, [current]);

  // Back from the Firebase SAML handler (or with a session Firebase still holds):
  // exchange the JWT without asking again. `resumed` keeps it to one attempt —
  // getRedirectResult() reports the credential once per page load.
  //
  // The action is installed by GlobalProvider's own effect, which runs AFTER this
  // one on the first commit, so it is undefined here to begin with. Never latch
  // `resumed` on that pass: doing so spends the single attempt on a no-op and
  // leaves the button disabled forever.
  const resumed = useRef(false);
  useEffect(() => {
    // `pending` is load-bearing, not cosmetic. `fetchFactory` bails out silently on
    // `if (!token || pending) return` — no request, no dispatch — and the exchange
    // below closes over whatever `user.pending` was when the action was built. On the
    // first commit that is the initial `true`, so firing now sends nothing and still
    // reports success, which re-arms the spinner for good. SingleSignOnScreen has
    // always waited for this via `mayLogin`; this effect has to as well.
    if (!firebaseMode || token || pending) return;
    if (!completeFirebaseLogin) {
      // Nothing to resume with yet (or at all) — let the button be pressed.
      // testAction 5 holds the in-flight state for Storybook, where the context
      // action never arrives at all.
      if (testAction !== 5) setPendingRedirect(false);
      return;
    }
    if (resumed.current) return;
    resumed.current = true;
    // Re-arm: the branch above cleared this while waiting for the action, and the
    // exchange below is a real round trip (SDK import, getRedirectResult, then the
    // POST). Leaving the button live through it would let a click start a second
    // SAML redirect alongside the one already resolving.
    setPendingRedirect(true);
    completeFirebaseLogin()
      .then((didResume) => setPendingRedirect(didResume))
      .catch(() => {
        setPendingRedirect(false);
        setSsoFailed(true);
      });
  }, [firebaseMode, token, pending, completeFirebaseLogin, testAction]);

  const handleEmailChange = useCallback((event: ChangeEvent<{ name?: string; value: unknown; }>) => {
    event?.preventDefault();
    setEmail(toString(event?.target?.value));
    setInvalidEmail(false);
  }, []);

  const handleClick = useCallback(() => {
    if (firebaseMode) {
      // Navigates the whole tab to the Firebase SAML handler; no popup.
      // Cleared first so a second identical failure re-opens the snackbar.
      setSsoFailed(false);
      setPendingRedirect(true);
      loginWithFirebase?.().catch(() => {
        setPendingRedirect(false);
        setSsoFailed(true);
      });
    } else if (hasMultipleSSO) {
      setInvalidEmail(false);
      setPendingRedirect(true);
      saveEmail(email);
      verifyEmail?.({
        variables: { email },
        onCompleted: (data) => {
          if (data?.verifyEmail?.success) {
            window.location.assign(`${PATH_LOGIN_LINK_SSO}?email=${encodeURIComponent(email)}`);
          } else {
            setInvalidEmail(true);
            setPendingRedirect(false);
          }
        },
        onError: () => {
          setInvalidEmail(false);
          setPendingRedirect(false);
        }
      });
    } else sendAuthEmail?.({
      email,
      onSuccess: () => setWaiting(true)
    });
  }, [email, verifyEmail, sendAuthEmail, hasMultipleSSO, firebaseMode, loginWithFirebase]);

  useEffect(() => {
    if (testAction === 2) verifyEmail?.({ variables: { email: 'test@example.com' } });
  }, [testAction, verifyEmail]);

  if (token && user && !pending && !failed) return <Navigate replace to={getRedirectionPath(user)}/>;

  return pending ? <LinearProgress/> : (
    <>
      <Screen appbar="empty" splash>
        <SplashText
            variant={splashVariant(firebaseMode, hasMultipleSSO, waiting)}
            name={email}
            onClick={waiting ? undefined : handleClick}
            pending={pendingAuthEmail || pendingAction}
            disabled={firebaseMode ? false : !isValidEmail(email)}
        >
          {!waiting && !firebaseMode && (
            <Box sx={inputContainerSx}>
              <TextField
                  inputRef={inputRef}
                  variant="standard"
                  color="secondary"
                  margin="normal"
                  fullWidth
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  label={emailLabel}
                  onChange={handleEmailChange}
                  value={email}
                  disabled={pendingAuthEmail || pendingAction ? true : undefined}
              />
            </Box>
          )}
        </SplashText>
      </Screen>
      <ActionSnackbar
          severity="error"
          message="splash.multiple_sso.error"
          open={invalidEmail}
      />
      <ActionSnackbar
          severity="error"
          message="splash.multiple_sso.verify.error"
          open={failedVerify}
      />
      <ActionSnackbar
          severity="error"
          long
          message="splash.sso.error"
          open={ssoFailed}
      />
      <ActionSnackbar
          severity="error"
          long
          message="splash.login.error"
          open={failedAuthEmail}
      />
    </>
  );
};

export default memo(LoginScreen);
